/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Preference} from '@ion/core/src/types/preferences';
import {isBottomSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {setSideSheetStatus} from '../../../store/actions/nav';
import {
  changeAssetsSortingOrder,
  getCVSPaginatedAssets,
  refreshListAssets,
  setSideSheetView
} from '../../../store/actions/tracking';
import {
  isLargeFleetModeSelector,
  scDevicesSelector,
  sideSheetViewSelector,
  trackingAssetsSortDescendingSelector
} from '../../../store/selectors/tracking-selectors';
import {getRoute, Route} from '../../RouteDefinitions';
import {useAssetlength} from '../hooks/sidesheet/useAssetlength';

/**
 *
 * The custom hook to provide generic util functions that are used for
 *  in sidesheet/resizable bottomsheet header.
 *
 *  ```typescript
 *
 *   import {useSheetHeader} from '@/routes/tracking/sidesheet';
 *
 *   const sheetOptions = useSheetHeader();
 *
 *  ```
 */
export const useSheetHeader = () => {
  const dispatch               = useDispatch();
  const {isDesktop, isPhone}   = useMedia();
  const isLargeFleetMode       = useSelector(isLargeFleetModeSelector);
  const sideSheetView          = useSelector(sideSheetViewSelector);
  const scDevices              = useSelector(scDevicesSelector) || [];
  const isAssetsSortDescending = useSelector(trackingAssetsSortDescendingSelector);
  const resultModuleLabel      = useAssetlength(isLargeFleetMode, scDevices, sideSheetView);

  const onSort = useCallback(function() {
    dispatch(changeAssetsSortingOrder(!isAssetsSortDescending));
    if (isLargeFleetMode) {
      dispatch(getCVSPaginatedAssets({startRow: 0, forceUpdate: true}) as any);
    }
  }, [isAssetsSortDescending, isLargeFleetMode]);

  const toggleListView = useCallback(function(e) {
    const {currentTarget: {id}} = e;
    UserPreferences.set(Preference.trackingAssetViewMode, id, true);
    dispatch(setSideSheetView(id));
    const open = !isBottomSheetTableView(id);
    dispatch(setSideSheetStatus({
      activeSideSheet      : getRoute(Route.TRACKING),
      mobileSideSheetOpen  : isPhone && open,
      desktopSideSheetOpen : isDesktop && open
    }));
  }, [isPhone, isDesktop]);

  // returns the dispatch call's own promise so callers can await the actual
  // fetch completing (e.g. to know when to resume the update progress bar
  // and start its next cycle) rather than firing-and-forgetting it ...
  const onRefreshAssets = useCallback(function() {
    return dispatch(refreshListAssets() as any);
  }, []);

  const onGeofenceOrLocationSearch = useCallback(() => {
    dispatch(setSideSheetStatus({
      activeSideSheet     : getRoute(Route.TRACKING),
      mobileSideSheetOpen : false
    }));
  }, []);

  return {
    onGeofenceOrLocationSearch,
    resultModuleLabel,
    toggleListView,
    onRefreshAssets,
    onSort,
    isAssetsSortDescending
  };
};

export default useSheetHeader;
